'use client'

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import {
  useQueryState,
  useResponsive,
  useTranslations,
  useLayout,
  QueryStateProvider
} from '@dustin-web/microsite-components/src/hooks'
import { ProductInfinityList } from '@dustin-web/product-components/src/product-list/product-infinity-list'
import { ProductListOptions, getSortBy } from '@dustin-web/product-components/src/product-list'
import { BOOLEAN_FACET_NAME, FilterBox, Filters } from '@dustin-web/product-components/src/filters'
import { ActiveFilters } from '@dustin-web/product-components/src/filters/active-filters'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import Button from '@dustin-web/microsite-components/src/button'
import classNames from 'classnames'
import { Breadcrumbs } from '@dustin-web/microsite-components/src/breadcrumbs'
import { useGraphqlInfinite } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import { SoftCategoryContentType } from '@contentsite/src/graphql/generated/graphql-types'
import MacroContent from '@contentsite/src/components/macros/macro-content'
import cssToJs from '@contentsite/src/components/utils/css-to-js'
import { SoftCategoryContentSchema } from '@contentsite/src/components/schemas/collection-page-schema'
import type { ProductSearch } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { SearchPage } from '@contentsite/src/graphql/search-page.graphql'
import { isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'
import type {
  SearchPageQueryVariables,
  SearchPageQuery,
} from '@contentsite/src/graphql/generated/graphql-types'

type SoftCategoryContentItem = {
  categoryPath?: string | null
  contentHeader?: string | null
  contentType: SoftCategoryContentType
  textContent?: string | null
  cssClass?: string | null
  cssStyle?: string | null
  outerCssClass?: string | null
  outerCssStyle?: string | null
}

type Props = {
  page: {
    title?: string
    pageHeader?: string | null
    metaDescription?: string | null
    metaKeywords?: string | null
    noIndex?: boolean
    breadcrumbs?: Array<{
      displayName?: string
      url?: string
    }>
    softCategoryContent?: Array<SoftCategoryContentItem>
  }
  pageData?: {
    productSearch?: ProductSearch
  }
}

const SoftCategoryContentPageWrapper = ({ page, pageData }: Props) => {
  const searchParams = useSearchParams()
  const productFilterContent = page?.softCategoryContent?.find(
    c => c.contentType === SoftCategoryContentType.ProductFilterBlock
  )
  const categoryPath = productFilterContent?.categoryPath || ''

  const searchParamsFromContent = new URLSearchParams(categoryPath.split('?')[1] ?? '')
  const query = Object.fromEntries([...(searchParams?.entries() ?? []), ...searchParamsFromContent.entries()])

  return (
    <QueryStateProvider query={query}>
      <SoftCategoryContentPage page={page} pageData={pageData} />
    </QueryStateProvider>
  )
}

const SoftCategoryContentPage = ({ page, pageData }: Props) => {
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const { queryState, setQueryState } = useQueryState()
  const { responsive } = useLayout()
  const { translate } = useTranslations()
  const { breakpoints } = useResponsive()
  const filterRef = useRef<HTMLDivElement | null>(null)
  const productListRef = useRef<HTMLDivElement | null>(null)
  const [pageNumber, setPageNumber] = useState(queryState.page)

  const productFilterContent = page?.softCategoryContent?.find(
    c => c.contentType === SoftCategoryContentType.ProductFilterBlock
  )

  const { activeFilters, searchwithin, sortby } = queryState

  const categoryPath = productFilterContent?.categoryPath || ''
  const [baseCategoryPath] = categoryPath.split('?')

  const variables = {
    searchPhrase: searchwithin || '',
    sortBy: getSortBy(sortby || 'sales'),
    page: queryState.page || 1,
    pageSize: 18,
    facets: activeFilters,
    selectedCategoryPath: baseCategoryPath,
  }

  const { data, error, size, setSize, isValidating } = useGraphqlInfinite<
    SearchPageQuery,
    SearchPageQueryVariables
  >(SearchPage, {
    variables,
    swrOptions: {
      ...(pageData ? { fallbackData: [pageData] } : {}),
      initialSize: queryState.page || 1,
      revalidateFirstPage: false,
    },
  })

  // Use client-side data if available, fallback to server-side data
  const productSearch = (data && data[0]?.productSearch) ?? pageData?.productSearch

  const productsToShow = useMemo(() => {
    return data
      ? data.map(d => d?.productSearch?.productReferences?.filter(
          (product: any) => product?.searchResultProduct?.availability?.isAvailableForSale
        )).filter(isNotNullOrUndefined)
      : [pageData?.productSearch?.productReferences?.filter(
          (product: any) => product?.searchResultProduct?.availability?.isAvailableForSale
        ) || []]
  }, [data, pageData?.productSearch?.productReferences])

  const products = useMemo(() => productsToShow.flat(), [productsToShow])

  const { facets = [] } = productSearch ?? {}

  useEffect(() => {
    const filters = activeFilters.filter(f => f.name !== 'path')
    setHasActiveFilters(filters.length > 0)
  }, [activeFilters])

  const clearFilters = useCallback(() => {
    const keys = activeFilters.reduce((acc, activeFilter) => {
      acc[activeFilter.name] = ''
      return acc
    }, {} as { [key: string]: string })

    keys[BOOLEAN_FACET_NAME] = ''
    keys.page = ''

    setQueryState(keys)
  }, [activeFilters, setQueryState])

  const handleLoadMore = useCallback(() => {
    setSize(size + 1)
    setPageNumber(size + 1)
  }, [size, setSize])

  if (error) {
    throw error
  }

  return (
    <div id="page-container" className="c-filter" data-gasource="CategoryListingPage">
      <SoftCategoryContentSchema page={page} />
      <div className="container">
        <div className="row">
          <div className="col-12">
            {productFilterContent?.categoryPath && (
              <Breadcrumbs
                path={productFilterContent.categoryPath
                  .split('?')[0]
                  .split('/')
                  .map((segment, index, array) => ({
                    displayName:
                      segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
                    url: '/group/' + array.slice(0, index + 1).join('/'),
                  clientNavigationRoute: '/group/' + array.slice(0, index + 1).join('/')
                  }))}
              />
            )}
            {!page?.softCategoryContent?.some(c => c.contentType === SoftCategoryContentType.HtmlBlock) && (
              <h1 className="h2 u-mb-16 u-pt-16 u-pt-lg-0 u-pl-16 u-pl-lg-0">
                {page?.pageHeader || productFilterContent?.contentHeader}
              </h1>
            )}
          </div>
        </div>
      </div>

      {page?.softCategoryContent
        ?.filter(c => c.contentType === SoftCategoryContentType.HtmlBlock)
        .map((htmlBlock, index) => {
          const outerClassName = htmlBlock.outerCssStyle ?? ''
          const outerStyleStr = htmlBlock.outerCssClass ?? ''
          return (
            <div key={index} className={outerClassName || undefined} style={cssToJs(outerStyleStr)}>
              <div className="container" style={cssToJs(htmlBlock.cssStyle)}>
                <div className="row">
                  <MacroContent content={htmlBlock.textContent ?? ''} />
                </div>
              </div>
            </div>
          )
        })
      }

      <div className="container u-mb-24">
        <div className="row sm-gutters">
          <Drawer
            visible={showMobileFilter && responsive.isSmallScreen}
            onClose={() => setShowMobileFilter(false)}
            returnOnlyChildren={!responsive.isSmallScreen}
            className="row no-gutters u-px-0 u-py-0">
            <div className="col-12 col-lg-3">
              {facets.length > 0 && (
                <FilterBox
                  ref={filterRef}
                  title={translate('mobilefilterlabel')}
                  className={responsive.isSmallScreen ? 'u-bt u-border-light' : ''}
                  titleClassName={responsive.isSmallScreen ? 'u-fs-16' : ''}
                  headerClassName={responsive.isSmallScreen && hasActiveFilters ? 'u-pb-8' : ''}
                  additionalTopContent={
                    <Button
                      defaultBtn
                      className={classNames('button-stripped p-sm u-mb-0', {
                        'u-decoration-none': !hasActiveFilters,
                      })}
                      text={translate('ResetAllFilters')}
                      onClick={clearFilters}
                      disabled={!hasActiveFilters}></Button>
                  }
                  activeFilters={
                    showMobileFilter ? (
                      <ActiveFilters facetGroups={facets || []} mobile />
                    ) : null
                  }>
                  <Filters facets={facets} />
                </FilterBox>
              )}
            </div>
          </Drawer>

          <div id="filter-product-list" className={classNames(facets.length > 0 ? 'col-12 col-lg-9' : 'col-12')}>
            <ProductListOptions
              disablePageSize
              listCategoryName={page?.pageHeader || page?.title || 'Soft Category'}
              listCategoryId={productFilterContent?.categoryPath?.split('?')[0] || ''}
              onMobileFilterOverlayOpen={() => setShowMobileFilter(true)}
            />

            <div ref={productListRef} className="u-mb-16">
              {products.length > 0 ? (
                <ProductInfinityList
                  showSideButtons={breakpoints.max.SM}
                  handleLoadMore={handleLoadMore}
                  totalHits={productSearch?.productHits || products.length}
                  productReferences={productsToShow}
                  isLoadingMore={isValidating}
                  searchPhrase={searchwithin || ''}
                  digitalDataOrigin={'categoryPage' as const}
                  page={pageNumber || 1}
                  hTag={'h2'}
                />
              ) : (
                <div className="text-center u-py-64">
                  <h3>{translate('NoProductsFound')}</h3>
                  <p className="text-muted">{translate('TryAdjustingFilters')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default SoftCategoryContentPageWrapper
